import React, { useState } from "react";
import { CheckCircle, Loader2 } from "lucide-react";

interface Tier {
  name: string;
  description: string;
  features: string[];
  price: string;
}

const tiers: Tier[] = [
  {
    name: "Pro",
    description: "Perfect for small teams and individuals.",
    features: ["Basic analytics", "Priority support", "1 team workspace"],
    price: "$20/month",
  },
  {
    name: "Premium",
    description: "Best for growing businesses.",
    features: ["Advanced analytics", "24/7 support", "3 team workspaces"],
    price: "$50/month",
  },
  {
    name: "Enterprise",
    description: "Ideal for large organizations with custom needs.",
    features: [
      "Custom analytics",
      "Dedicated account manager",
      "Unlimited workspaces",
    ],
    price: "Contact Us",
  },
];

const SubscriptionTierSelection: React.FC = () => {
  const [selectedTier, setSelectedTier] = useState<string>("Free Trial");
  const [loadingTier, setLoadingTier] = useState<string | null>(null);

  const handleSelectTier = (tierName: string) => {
    if (tierName !== selectedTier) {
      setLoadingTier(tierName);
      setTimeout(() => {
        setSelectedTier(tierName);
        setLoadingTier(null);
      }, 1000);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark flex flex-col items-center px-6 py-12">
      <div className="text-center mb-12 max-w-2xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-2">
          Choose Your Plan
        </h2>
        <p className="text-sm md:text-base text-text-secondary dark:text-text-secondaryDark">
          Select the subscription tier that fits your needs. You can upgrade or
          downgrade anytime to unlock features tailored to your success.
        </p>
      </div>

      <div className="flex items-center justify-center mb-8 space-x-2">
        <CheckCircle className="w-5 h-5 text-green-500" />
        <span className="font-semibold text-sm text-green-600 dark:text-green-400">
          {selectedTier} Active
        </span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl w-full">
        {tiers.map((tier) => {
          const isSelected = tier.name === selectedTier;
          const isLoading = loadingTier === tier.name;

          return (
            <div
              key={tier.name}
              className={`relative flex flex-col justify-between
                bg-background-paper dark:bg-background-paperDark
                border border-divider dark:border-divider-dark
                rounded-lg p-6 shadow-sm transition-all hover:shadow-md
                ${isSelected ? "ring-4 ring-secondary" : ""}
              `}
            >
              <div>
                <h3 className="text-xl font-semibold mb-2 text-text-primary dark:text-text-primaryDark">
                  {tier.name}
                </h3>
                <p className="text-sm text-text-secondary dark:text-text-secondaryDark mb-4">
                  {tier.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <CheckCircle className="w-4 h-4 text-secondary mr-2 flex-shrink-0 mt-[2px]" />
                      <span className="text-text-primary dark:text-text-primaryDark">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="text-2xl font-bold text-secondary">
                  {tier.price}
                </p>
              </div>

              <button
                onClick={() => handleSelectTier(tier.name)}
                disabled={isSelected || isLoading}
                className={`mt-6 py-2 px-4 rounded-md text-sm font-semibold transition-colors ${
                  isSelected
                    ? "bg-divider dark:bg-divider-dark text-text-secondary dark:text-text-secondaryDark cursor-default"
                    : "bg-secondary text-white hover:bg-secondary/90"
                }`}
              >
                {isLoading ? (
                  <Loader2 className="animate-spin w-5 h-5 mx-auto" />
                ) : isSelected ? (
                  "Current Plan"
                ) : (
                  "Select Plan"
                )}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-sm text-text-secondary dark:text-text-secondaryDark mt-12 text-center max-w-lg">
        Unsure which plan is right for you?
        <span className="font-medium text-secondary cursor-pointer ml-1 hover:underline">
          Contact us
        </span>{" "}
        for personalized guidance. By upgrading, you agree to our{" "}
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary hover:underline"
        >
          Terms of Service
        </a>
        .
      </p>
    </div>
  );
};

export default SubscriptionTierSelection;
